import { CoreEvent, eventNames, useCoreEvents } from '@carvana/core-events';
import { SearchSource } from '@carvana/merch-contracts';
import { useEffectOnce } from '@carvana/merch-react-hooks';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useContext, useEffect } from 'react';
import { COMPOSER_OPEN_PARAM } from '~/constants/composer';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import {
  useAppliedFilters,
  useAppliedFiltersContext
} from '~/context/common/AppliedFiltersContext';
import { useIsNative } from '~/context/common/DeviceContext';
import { useEventCorrelationId } from '~/context/common/StaticContext';
import { useTriggerVdpNavigation } from '~/context/common/VdpNavigationContext';
import {
  useHasInventoryError,
  useInventoryIsLoaded,
  useInventoryVehicles,
  useRequestIds,
  useTotalMatchedInventory,
  useUserDeliveryInfo
} from '~/context/srp/InventoryContext';
import ModalContext from '~/context/srp/ModalContext';
import { ComposerModalName } from '~/context/srp/ModalContext/constants';
import SearchEventsDataContext from '~/context/srp/SearchEventsDataContext';
import type { AppliedFilters } from '~/contracts/common';
import useComposerOpenReady from '~/hooks/common/composer/useComposerOpenReady';
import {
  SoftPullLocations,
  useOpenSoftPullModal
} from '~/hooks/srp/useToggleSoftPullModal';
import buildComposerSearchResults from '~/utilities/composer/buildComposerSearchResults';
import buildComposerSuggestionInfo from '~/utilities/composer/buildComposerSuggestionInfo';
import buildSearchLink from '~/utilities/composer/buildSearchLink';
import parseComposerFilters from '~/utilities/composer/parseComposerFilters';
import decodeUrlFilters from '~/utilities/decodeUrlFilters';
import { logError } from '~/utilities/loggers';
import { markSpaNavigationStart } from '~/utilities/navigationTiming';

export default function useComposerEvents() {
  const { setComposerUiModalOpen, setGeolocationModalOpen } =
    useContext(ModalContext);
  const openSoftPullModal = useOpenSoftPullModal(SoftPullLocations.Composer);
  const { isReady: isComposerReady, whenReady } = useComposerOpenReady();
  const eventCorrelationId = useEventCorrelationId();
  const isNative = useIsNative();
  const router = useRouter();
  const searchParams = useSearchParams();
  const triggerVdpNavigation = useTriggerVdpNavigation();
  const filters = useAppliedFilters();
  const { setAppliedFilters, setSortBy, sortBy } = useAppliedFiltersContext();
  const { setKeywordSuggestion, setSearchSource } = useContext(
    SearchEventsDataContext
  );
  const { searchRequestId } = useRequestIds();
  const totalMatchedInventory = useTotalMatchedInventory();
  const deliveryInfo = useUserDeliveryInfo();
  const hasInventoryError = useHasInventoryError();
  const inventoryIsLoaded = useInventoryIsLoaded();
  const trackEvent = useTrackEvent();
  const vehicles = useInventoryVehicles();

  const activeFilterCount = (
    Object.keys(filters) as Array<keyof AppliedFilters>
  ).filter(key => filters[key] != null).length;

  const closeComposerShell = useCallback(() => {
    CoreEvent.emit(eventNames.composer.close, { source: 'page' });
    trackEvent('composer_shell_closed', { page: 'srp' });
  }, [trackEvent]);

  useCoreEvents(
    eventNames.composer.mfe.opened,
    useCallback(
      ({ source }) => {
        if (source !== 'composer') return;

        setComposerUiModalOpen(true);
      },
      [setComposerUiModalOpen]
    )
  );
  useCoreEvents(
    eventNames.composer.mfe.closed,
    useCallback(
      ({ source }) => {
        if (source !== 'composer') return;

        setComposerUiModalOpen(false);
      },
      [setComposerUiModalOpen]
    )
  );
  useCoreEvents(
    eventNames.composer.mfe.openPageModal,
    useCallback(
      ({ modal, source }) => {
        if (source !== 'composer') return;

        switch (modal) {
          case ComposerModalName.Geolocation:
            setGeolocationModalOpen(true, 'composer');
            break;
          case ComposerModalName.SoftPull:
            openSoftPullModal();
            break;
          default:
            logError(new Error('Unknown modal'), { modal });
        }
      },
      [openSoftPullModal, setGeolocationModalOpen]
    )
  );
  useCoreEvents(
    eventNames.composer.mfe.updateSearch,
    useCallback(
      ({ cvnaid, filters: updatedFilters, source, suggestionInfo }) => {
        if (source !== 'composer') return;

        const keywordSuggestion =
          suggestionInfo && buildComposerSuggestionInfo(suggestionInfo);

        if (cvnaid) {
          const url = new URL(
            buildSearchLink({ cvnaid }),
            window.location.origin
          );
          const { filters: decodedFilters, sortBy: decodedSortBy } =
            decodeUrlFilters(url);

          setSearchSource(SearchSource.Composer);
          if (keywordSuggestion) setKeywordSuggestion(keywordSuggestion);
          setAppliedFilters(decodedFilters);
          setSortBy(decodedSortBy);

          return;
        }

        const validatedFilters = parseComposerFilters(updatedFilters);
        if (!validatedFilters) return;

        setSearchSource(SearchSource.Composer);
        if (keywordSuggestion) setKeywordSuggestion(keywordSuggestion);
        setAppliedFilters(validatedFilters);
      },
      [setAppliedFilters, setKeywordSuggestion, setSearchSource, setSortBy]
    )
  );
  // SRP -> VDP stays an SPA nav (same app-router layout), so composer persists
  // in the background across it — explicitly close it so it doesn't land on
  // VDP still expanded from the SRP page.
  useCoreEvents(
    eventNames.composer.mfe.vdp,
    useCallback(
      ({ source, vehicleId }) => {
        if (source !== 'composer' || isNative || !vehicleId) return;

        triggerVdpNavigation();
        markSpaNavigationStart('navigate');
        router.push(`/vehicle/${vehicleId}`);
        closeComposerShell();
      },
      [closeComposerShell, isNative, router, triggerVdpNavigation]
    )
  );

  useEffect(() => {
    if (!isComposerReady) return;

    CoreEvent.emit(eventNames.composer.pageContext, {
      context: {
        activeFilterCount,
        capabilities: ['handle', 'openPageModal', 'update_search', 'vdp'],
        filters,
        merchContextId: eventCorrelationId,
        searchRequestId,
        sort: sortBy,
        supportedModals: [
          ComposerModalName.Geolocation,
          ComposerModalName.SoftPull
        ],
        totalMatchedInventory,
        url: window.location.href,
        zip5: deliveryInfo?.zip5
      },
      source: 'page',
      surface: 'srp'
    });
  }, [
    activeFilterCount,
    deliveryInfo?.zip5,
    eventCorrelationId,
    filters,
    isComposerReady,
    searchRequestId,
    sortBy,
    totalMatchedInventory
  ]);

  useEffect(() => {
    if (!isComposerReady || !inventoryIsLoaded || hasInventoryError) return;

    CoreEvent.emit(eventNames.sebastian.searchResultsLoaded, {
      vehicles: buildComposerSearchResults(vehicles),
      source: 'page'
    });
  }, [hasInventoryError, inventoryIsLoaded, isComposerReady, vehicles]);

  // Runs once on arrival — if composer isn't ready yet, useComposerOpenReady
  // queues the open and fires it itself once ready, same as the manual open
  // button, instead of this effect re-checking readiness on every render.
  // composer_open is stripped from the URL by useSyncUrl (paramsToRemove), not here.
  useEffectOnce(() => {
    if (searchParams?.get(COMPOSER_OPEN_PARAM) !== '1') return;

    whenReady(() => {
      CoreEvent.emit(eventNames.composer.open, { source: 'page' });
    });
  }, [searchParams, whenReady]);
}
